import { DurationString, isDurationString, isTimeRangeInputValueAbsolute } from '@chronosphereio/core';
import { useMemo } from 'react';
import { DashboardTimeDefinition, isValidDate, PanelDefinition } from '@/core';
import { useReplaceVariablesInString, useTimeRange } from '@/plugin-system';

/**
 * Check query_options for relative time override.
 * When parent time range is absolute, relative time override is ignored.
 */
export function useRelativeTimeQueryOption(panelDefinition: PanelDefinition): DurationString | undefined {
  const { timeRange } = useTimeRange();
  const queryOptions = panelDefinition.spec.query_options;
  // Variables can be used in time override query option, ensure they still resolve to a duration.
  const timeOption = useReplaceVariablesInString(queryOptions?.time?.start);
  const relativeTimeQueryOption = useMemo(() => {
    if (isTimeRangeInputValueAbsolute(timeRange)) return undefined;
    if (timeOption === undefined) return undefined;
    if (isDurationString(timeOption)) {
      return timeOption;
    }
  }, [timeOption, timeRange]);
  return relativeTimeQueryOption;
}

/**
 * Used to check whether a valid absolute time range is set in 'time' top level property of dashboard spec.
 */
export function isValidAbsoluteDashboardTime(maybeTime: unknown): maybeTime is DashboardTimeDefinition {
  if (!maybeTime || typeof maybeTime !== 'object') return false;

  const time = maybeTime as Record<string, unknown>;

  if (typeof time.start !== 'string' || typeof time.end !== 'string') {
    return false;
  }

  const startDate = new Date(time.start);
  const endDate = new Date(time.end);
  return isValidDate(startDate) && isValidDate(endDate);
}
